import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { formatDate } from 'utils/dayjs';
import { getCountryName } from 'utils/formatters/getCountryName';
import { hasValue } from 'utils/hasValue';

import type { CollectionPaymentStatus, CollectionStatusHistoryItem } from 'api';
import { useGetCollectionPaymentsUuid } from 'api';
import { convertTypeToLabel } from 'constants/paymentType';

import { useOrganisationId } from 'hooks/context';

import { PaymentBadge } from '@atoms/badges/PaymentBadge/PaymentBadge';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

import { Loading } from 'components/pages/Loading';
import type { Item } from 'components/ui/organisms/DetailsBox';
import { DetailsBox } from 'components/ui/organisms/DetailsBox';

import { ORDER_PAYMENT_DETAILS_TEST_ID } from './constants';

export const PaymentDetails = () => {
  const params = useParams();
  const organisationId = useOrganisationId<number>();

  const paymentId = params?.['*']?.split('/')[1];

  const {
    data: { data: paymentData } = {},
    isLoading,
    isError,
    isSuccess
  } = useGetCollectionPaymentsUuid(organisationId, paymentId!, {
    query: { enabled: hasValue(paymentId) }
  });

  const data = useMemo<Array<Item>>(() => {
    const sections: Array<Item> = [];
    if (!paymentData || !isSuccess) return sections;

    sections.push({
      header: 'Payment information',
      key: 'info',
      data: [
        {
          key: 'Payment type',
          value: convertTypeToLabel(paymentData.type)
        },
        {
          key: 'Debtor country',
          value: getCountryName(paymentData.details?.debtor_country ?? '') ?? ''
        },
        {
          key: 'Debtor bank',
          value: paymentData.details?.debtor_bank_name ?? ''
        }
      ]
    });

    // TODO:We need to improve naming to pass items to the sections.
    sections.push({
      header: 'Debtor information',
      key: 'debtor',
      data: [
        {
          key: 'Name',
          value: paymentData.order.debtor.full_name
        },
        {
          key: 'Date of birth',
          value: formatDate(paymentData.order.debtor.date_of_birth!)
        },
        {
          key: 'Email',
          value: paymentData.order.debtor.email
        },
        {
          key: 'Country of Residence',
          value:
            getCountryName(paymentData.order.debtor.country_of_residence!) ||
            paymentData.order.debtor.country_of_residence
        }
      ]
    });
    sections.push({
      header: 'History',
      key: 'history',
      data: [],
      customChildren: paymentData.status_history?.map(
        (transition: CollectionStatusHistoryItem, index: number) => (
          <div
            className="flex justify-between pt-6 last-of-type:pb-6"
            key={index}
          >
            <div className="flex flex-col justify-between gap-1">
              <div className="text-xs">Status</div>
              <PaymentBadge
                type={transition?.status as CollectionPaymentStatus}
              />
            </div>
            <div className="flex flex-col justify-end">
              <FormattedDateTime value={transition?.created_at} />
            </div>
          </div>
        )
      )
    });

    return sections;
  }, [paymentData, isSuccess]);

  if (!paymentId) return null;

  return (
    <div
      role="complementary"
      aria-label="Payment details"
      className="cool-shadow flex max-h-full w-full flex-col overflow-hidden rounded-xl bg-white"
      style={{ flex: 0.25 }}
      data-testid={ORDER_PAYMENT_DETAILS_TEST_ID}
    >
      <div className="payment-overview__header">
        <div className="flex items-center justify-between px-6 py-3.5">
          <span>Details</span>
          <Link to=".." className="cursor-pointer" aria-label="Close">
            <XMarkIcon className="size-6" />
          </Link>
        </div>
      </div>
      <div className="overflow-auto">
        {isLoading && <Loading />}
        {isSuccess && <DetailsBox data={data} defaultValue="info" />}
        {isError && (
          <div className="flex h-full items-center justify-center">
            Something went wrong
          </div>
        )}
      </div>
    </div>
  );
};
